import * as Sentry from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { QUERY_KEY as USER_PROFILE_QUERY_KEY } from '~/services/useUserProfileQuery';
import { Role, UserProfile } from '~/services/types';
import { useHttpClient } from '~/hooks/useHttpClient';
import { sanitizeTagValue } from '~/utils/instrumentation';
import { createLogger } from '~/utils/logging';
import { ClientError } from '~/errors';
import { trackEvent, trackConversion } from '~/gtag';
import { tokenManager } from '~/services/TokenManager';

const log = createLogger('useEmailPasswordLoginMutation');

export type EmailPasswordLogInRequest = {
  email: string;
  password: string;
};

export type EmailPasswordLogInResponse = {
  user: UserProfile;
  activeRole: Role;
  availableRoles: Role[];
  token: string;
};

export function useEmailPasswordLoginMutation() {
  const queryClient = useQueryClient();
  const httpClient = useHttpClient();

  const { data, error, mutate, reset, status } = useMutation({
    mutationFn: async (request: EmailPasswordLogInRequest) => {
      log.debug('Logging in user with email/password', { email: request.email });
      const response = await httpClient.post<EmailPasswordLogInResponse>('/auth/logIn', request, {
        credentials: 'include',
      });
      log.info('Logged in user successfully', {
        userId: response.user.id,
      });
      return response;
    },
    onSuccess: (data) => {
      log.debug('Setting token and user profile', { userId: data.user.id });
      tokenManager.setToken(data.token);
      queryClient.setQueryData([USER_PROFILE_QUERY_KEY], {
        user: data.user,
        activeRole: data.activeRole,
        availableRoles: data.availableRoles,
      });
      log.debug('Set token and user profile');
      Sentry.setUser({ id: data.user.id });
      log.debug('Set user in Sentry');
      trackEvent('login');
      trackConversion('login');
    },
    onError: (err: Error) => {
      if (err instanceof ClientError) {
        // Ignore client errors.
      } else {
        log.error('Failed to log in user', {
          errorMessage: sanitizeTagValue(err.message),
        });
      }
    },
  });

  return {
    error,
    mutate,
    reset,
    status,
    user: data?.user,
    token: data?.token,
  };
}
